/**
 * FrontierStrip — one-pass, four-cell strip that renders the winning
 * candidate for each of the four priorities.
 *
 * Contract (approved with the user):
 *   1. The cell for the ACTIVE priority renders immediately from the
 *      already-loaded /run result — no fetch, no spinner, never a
 *      placeholder number.
 *   2. The remaining three cells show a spinner until the single
 *      /api/configurator/frontier request lands. That endpoint
 *      computes all four cells in ONE server-side pass so the strip
 *      is internally consistent.
 *   3. Cache is keyed on input HASH server-side. A buyer who edits
 *      inputs sees a new hash → recompute; a buyer who only switches
 *      priority sees the same hash → cache hit → instant.
 *   4. If the fetch fails, the other cells stay hidden (spinner is
 *      replaced with a small "Alternatives unavailable" note). No
 *      guesses, no placeholders.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { canonicalPriority } from "@/lib/priority";
import { getLocale, t } from "@/lib/i18n";
import { candidateName } from "@/lib/resultsFr";
import { Loader2, Trophy } from "lucide-react";
import { num, money, moneyM, pct, unit, perUnit, perYr } from "@/lib/numfmt";

const PRIORITY_ORDER = [
  "lowest_upfront",
  "lowest_lifetime",
  "lowest_emission",
  "max_reliability",
];

const PRIORITY_LABEL = {
  lowest_upfront:  { title: "Lowest Upfront", accent: "#F6611C" },
  lowest_lifetime: { title: "Lowest Lifetime", accent: "#65B037" },
  lowest_emission: { title: "Lowest Emissions", accent: "#4CA6A8" },
  max_reliability: { title: "Max Reliability", accent: "#1A6F2B" },
};

const usd = (n) =>
  `${money(n || 0, 0)}`;

const CellShell = ({ prio, isActive, children }) => {
  const label = PRIORITY_LABEL[prio] || {};
  return (
    <div
      data-testid={`frontier-cell-${prio}`}
      className="frontier-cell"
      style={{
        border: `1px solid ${isActive ? label.accent : "var(--line)"}`,
        borderRadius: 10,
        padding: "10px 12px",
        background: isActive ? "rgba(101,176,55,0.05)" : "var(--paper)",
        position: "relative",
        minHeight: 108,
      }}
    >
      {isActive && (
        <div
          aria-hidden
          style={{
            position: "absolute", top: 6, right: 8,
            fontSize: 10, color: label.accent, display: "flex", gap: 3,
            alignItems: "center", fontWeight: 600, textTransform: "uppercase",
          }}
          data-testid={`frontier-cell-${prio}-active-badge`}
        >
          <Trophy size={10} /> {t("frontier.yours")}
        </div>
      )}
      <div
        style={{
          fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase",
          color: label.accent, fontWeight: 600, marginBottom: 4,
        }}
      >
        {getLocale() === "fr" ? t(`wiz.priority.${prio}`) : label.title}
      </div>
      {children}
    </div>
  );
};

const CellPending = ({ prio, isActive }) => (
  <CellShell prio={prio} isActive={isActive}>
    <div
      className="flex items-center justify-center"
      style={{ height: 68, color: "var(--muted)" }}
      data-testid={`frontier-cell-${prio}-spinner`}
    >
      <Loader2 size={18} className="animate-spin" />
    </div>
  </CellShell>
);

const CellUnavailable = ({ prio, isActive }) => (
  <CellShell prio={prio} isActive={isActive}>
    <div
      style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.4, paddingTop: 14 }}
      data-testid={`frontier-cell-${prio}-unavailable`}
    >
      {t("frontier.unavailable")}
    </div>
  </CellShell>
);

// a37 — the call failed twice: say so, rather than claim the cell is empty.
const CellLoadFailed = ({ prio, isActive }) => (
  <CellShell prio={prio} isActive={isActive}>
    <div
      style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.4, paddingTop: 14 }}
      data-testid={`frontier-cell-${prio}-load-failed`}
    >
      {getLocale() === "fr"
        ? "Impossible de charger cette comparaison. Rechargez la page pour réessayer."
        : "Couldn't load this comparison. Reload the page to try again."}
    </div>
  </CellShell>
);

// STEP 3 (RULED) — a cell is never blank: the not-viable state names its reason.
const CellNotViable = ({ prio, isActive, cell, lang }) => (
  <CellShell prio={prio} isActive={isActive}>
    <div
      style={{ fontSize: 11, color: "#7F1D1D", lineHeight: 1.4, paddingTop: 14 }}
      data-testid={`frontier-cell-${prio}-not-viable`}
    >
      {lang === "fr" ? "Non viable — voir ci-dessus" : "Not viable — see above"}
    </div>
  </CellShell>
);

// iter45as GA2 Part B / D60, D69 — the buyer-pinned cell.
//
// This strip exists to COMPARE SCORES. A pinned mix has none: the buyer typed
// the plant, it was never entered into the contest, and there is no CAPEX rank
// or 20-yr TCO to put against the other three cells. So the cell states what it
// is and seeds no numbers. A zero, a dash or a re-used figure from elsewhere
// would all read as a measurement.
const CellPinned = ({ prio, isActive }) => (
  <CellShell prio={prio} isActive={isActive}>
    <div
      style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.45, paddingTop: 8 }}
      data-testid={`frontier-cell-${prio}-buyer-pinned`}
    >
      <div style={{ fontWeight: 600, color: "var(--ink)" }}>{t("frontier.pinned_h")}</div>
      {t("frontier.pinned_body")}
    </div>
  </CellShell>
);

const CellFilled = ({ prio, cell, isActive, sizing }) => (
  <CellShell prio={prio} isActive={isActive}>
    <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}
         data-testid={`frontier-cell-${prio}-name`}>
      {candidateName(cell.name, sizing) || cell.kind}
    </div>
    {cell.floor_bound && (
      <div
        data-testid={`frontier-cell-${prio}-floor-badge`}
        title={t("frontier.floor_title")}
        style={{
          marginTop: 4, fontSize: 10, color: "#78350F",
          background: "#FEF3C7", border: "1px solid #F59E0B",
          borderRadius: 4, padding: "2px 6px", display: "inline-block",
          fontWeight: 600,
        }}
      >
        {t("frontier.floor")}
      </div>
    )}
    <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", fontSize: 11 }}>
      <div>
        <div style={{ color: "var(--muted)" }}>CAPEX</div>
        <div style={{ fontWeight: 600 }} data-testid={`frontier-cell-${prio}-capex`}>
          {usd(cell.capex_usd)}
        </div>
      </div>
      <div>
        <div style={{ color: "var(--muted)" }}>{t("frontier.tco")}</div>
        <div style={{ fontWeight: 600 }} data-testid={`frontier-cell-${prio}-tco`}>
          {usd(cell.tco_20yr_usd)}
        </div>
      </div>
      <div>
        <div style={{ color: "var(--muted)" }}>OPEX{perYr()}</div>
        <div style={{ fontWeight: 600 }} data-testid={`frontier-cell-${prio}-opex`}>
          {usd(cell.opex_usd_yr)}
        </div>
      </div>
      <div>
        <div style={{ color: "var(--muted)" }}>gCO₂/kWh</div>
        <div style={{ fontWeight: 600 }} data-testid={`frontier-cell-${prio}-ci`}>
          {Math.round(cell.co2_intensity_g_kwh)}
        </div>
      </div>
    </div>
  </CellShell>
);

export const FrontierStrip = ({ input, result }) => {
  const sizingByKind = Object.fromEntries((result?.candidates?.scoreboard || []).map((c) => [c.kind, c.sizing]));
  const rawPriority = input?.priority || "lowest_lifetime";
  const activePriority = canonicalPriority(rawPriority);
  const scored = result?.scored_candidate;
  // A pinned plant is in its own slot and carries no score (D60/D69). Read the
  // marker, never a cost field off it.
  const isPinned =
    result?.selected_configuration?.selection === "buyer_pinned";

  // Immediate seed: the ACTIVE cell renders from the loaded /run result.
  // Others start as `undefined` (pending); on fetch they become an
  // object or `null` (unavailable).
  const [cells, setCells] = useState(() => {
    const initial = {};
    for (const p of PRIORITY_ORDER) initial[p] = undefined;
    if (isPinned) {
      initial[activePriority] = { pinned: true };
    } else if (result?.no_viable_candidate) {
      // STEP 3: the active cell carries the same not-viable verdict as the result.
      initial[activePriority] = {
        not_viable: true,
        ...(result.no_viable_reason || { en: result.talk_to_engineer_reason, fr: null }),
      };
    } else if (scored) {
      initial[activePriority] = {
        kind: scored.kind,
        name: scored.name,
        capex_usd: scored.capex_usd,
        opex_usd_yr: scored.opex_usd_yr,
        tco_20yr_usd: scored.tco_20yr_usd,
        co2_intensity_g_kwh: scored.co2_intensity_g_kwh,
        co2_t_yr: scored.co2_t_yr,
      };
    }
    return initial;
  });
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (!input) return;
    let cancelled = false;
    (async () => {
      try {
        // a37 — one retry before the strip gives up on a failed call.
        const r = await api.post("/configurator/frontier", input)
          .catch(() => (cancelled ? Promise.reject(new Error("cancelled")) : api.post("/configurator/frontier", input)));
        if (cancelled) return;
        const remote = r.data?.cells || {};
        setCells((prev) => {
          const next = { ...prev };
          for (const p of PRIORITY_ORDER) {
            // Prefer server value for non-active cells; keep the seeded
            // scored_candidate on the active cell so the numbers on
            // screen match what the buyer already saw.
            if (p === activePriority) {
              // RULED Jun 2026 — the server now computes every cell from a run
              // whose input priority IS that cell's priority, so the active
              // cell MUST equal the hero we seeded from. Until Jun 2026 it did
              // not: the strip showed a lowest-lifetime cell $711,342 below the
              // lowest-lifetime hero on the same screen. The seed stays (it is
              // what removes the spinner where the buyer is looking), but a
              // disagreement is now a bug and says so out loud.
              const srv = remote[p];
              const seed = prev[p];
              if (srv && seed && !seed.pinned
                  && (srv.kind !== seed.kind
                      || Math.round(srv.capex_usd || 0) !== Math.round(seed.capex_usd || 0)
                      || Math.round(srv.tco_20yr_usd || 0) !== Math.round(seed.tco_20yr_usd || 0))) {
                // eslint-disable-next-line no-console
                console.error(
                  "[FrontierStrip] active cell disagrees with the hero it was "
                  + `seeded from (priority=${p}): server=`
                  + `${srv.kind}/${srv.capex_usd}/${srv.tco_20yr_usd} vs hero=`
                  + `${seed.kind}/${seed.capex_usd}/${seed.tco_20yr_usd}`);
              }
              continue;
            }
            next[p] = remote[p] || null;
          }
          return next;
        });
      } catch (e) {
        if (cancelled) return;
        setFetchError(true);
      }
    })();
    return () => { cancelled = true; };
    // Refire when the input hash-relevant fields change; priority-only
    // changes DON'T need a refetch (server hashes on non-priority fields).
    // Stringifying the input covers all hash-relevant edits at once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input && JSON.stringify({ ...input, priority: null })]);

  return (
    <section
      className="mt-6"
      data-testid="frontier-strip"
      aria-label={t("frontier.aria")}
    >
      <div
        className="text-xs mb-2"
        style={{ color: "var(--muted)", letterSpacing: "0.04em" }}
      >
        {t("frontier.title")}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 10,
        }}
      >
        {PRIORITY_ORDER.map((prio) => {
          const isActive = prio === activePriority;
          const cell = cells[prio];
          if (cell === undefined) {
            if (fetchError && !isActive) {
              return <CellLoadFailed key={prio} prio={prio} isActive={isActive} />;
            }
            return <CellPending key={prio} prio={prio} isActive={isActive} />;
          }
          if (cell === null) {
            return <CellUnavailable key={prio} prio={prio} isActive={isActive} />;
          }
          if (cell.not_viable) {
            return <CellNotViable key={prio} prio={prio} isActive={isActive} cell={cell}
              lang={getLocale()} />;
          }
          if (cell.pinned) {
            return <CellPinned key={prio} prio={prio} isActive={isActive} />;
          }
          return <CellFilled key={prio} prio={prio} cell={cell} isActive={isActive} sizing={sizingByKind[cell.kind]} />;
        })}
      </div>
    </section>
  );
};

export default FrontierStrip;
